import React, { useEffect, useState } from "react";

import { FaTimesCircle } from "react-icons/fa";

import { Step } from "../../components/Step";
import { Step1, Step2, Step3, Step4, StepAddDevice } from "./ConfigSteps";
import { createBuilding } from "../../apis/Building.Api";
import { createRoom } from "../../apis/Room.Api";
import { useDispatch, useSelector } from "react-redux";
import { addBuilding } from "../../features/slices/buildingSlice";
import { addRoom } from "../../features/slices/roomSlice";
import { addSensor } from "../../features/slices/sensorSlice";
import { selectUserData } from "../../features/store";
import { PERMISSION } from "../../configs/global";
import { addAuthorization } from "../../apis/Auth.Api";
import { useSnackbar } from "notistack";
import { Slide } from "@mui/material";
import { createDevice } from "../../apis/Device.Api";
import { createSensor } from "../../apis/Sensor.Api";
import { addDevice } from "../../features/slices/deviceSlice";

const AddConfig = ({ openAddConfig, setOpenAddConfig, duplicateConfig, setDuplicateConfig }) => {
    const { enqueueSnackbar } = useSnackbar();
    const userData = useSelector(selectUserData);
    const [step, setStep] = useState(0);
    const dispatch = useDispatch();
    function nextStep() {
        const nextStep = (step + 1) % 5;
        setStep(nextStep);
    }
    function prevStep() {
        if (step - 1 < 0) return;
        const nextStep = step - 1;
        setStep(nextStep);
    }
    // Handles if user close panel when api call is still running
    const [commitConfig, setCommitConfig] = useState(false);

    // step 1 states
    const [newBuilding, setNewBuilding] = useState({
        buildingName: "",
        buildingId: "",
    });
    const [selectedBuilding, setSelectedBuilding] = useState({
        buildingName: "",
        buildingId: "",
    });
    const [option, setOption] = useState(0); // 0 = select building, 1 = new building

    // step 2 states
    const [newRoom, setNewRoom] = useState({ roomId: "", roomName: "", floor: "" });

    // step 3 states
    const [newSensors, setNewSensors] = useState([]);

    // step 4 states
    const [newDevices, setNewDevices] = useState({
        f: "",
        p: "",
    });
    const [nanoOption, setNanoOption] = useState(0); // 0 = select nano, 1 = new nano
    const [selectedNano, setSelectedNano] = useState("");

    useEffect(() => {
        if (!duplicateConfig) return;
        setSelectedBuilding({
            buildingName: duplicateConfig.buildingName,
            buildingId: duplicateConfig.buildingId,
        });
        setNewRoom({
            roomId: duplicateConfig.roomId,
            roomName: duplicateConfig.roomName,
            floor: duplicateConfig.floor,
        });
        setNewSensors(duplicateConfig.sensorList);
        setNewDevices(duplicateConfig.deviceDict);
        setSelectedNano(duplicateConfig.nanoId);
    }, [duplicateConfig]);

    function resetStates() {
        setStep(0);
        setNewBuilding({
            buildingName: "",
            buildingId: "",
        });
        setSelectedBuilding({
            buildingName: "",
            buildingId: "",
        });
        setOption(0);
        setNewRoom({ roomId: "", roomName: "", floor: "" });
        setNewSensors([]);
        setNewDevices({ f: "", p: "" });
        setNanoOption(0);
        setSelectedNano("");
        setDuplicateConfig(null);
    }
    async function confirmAddConfig() {
        setCommitConfig(true);
        // API call
        // - create building if needed
        let token = userData.token;
        if (option === 1) {
            let r = await createBuilding({
                building_id: newBuilding.buildingId,
                building_name: newBuilding.buildingName,
            });
            if (r.data.networkError) {
                enqueueSnackbar(r.data.message, { variant: "error" });
                setCommitConfig(false);
                return;
            }
            if (!r.data.indicator) {
                enqueueSnackbar(
                    "Failed to add building. This is possibly due to duplicated building id or name. Room, sensors, and devices are not added.",
                    { variant: "error" }
                );
                setCommitConfig(false);
                return;
            }
            dispatch(addBuilding(newBuilding));
        }

        // create nano if needed
        let nanoToAdd = nanoOption === 0 ? selectedNano : newDevices["n"];
        if (nanoOption === 1) {
            let dRes = await createDevice({
                device_id: nanoToAdd,
                room_id: null,
            });
            if (dRes.data.networkError) {
                enqueueSnackbar(dRes.data.message, { variant: "error" });
                setCommitConfig(false);
                return;
            }
            if (!dRes.data.indicator) {
                enqueueSnackbar(`Failed to create nano ${nanoToAdd}. This is possibly due to duplicated device id.`, {
                    variant: "error",
                });
            }
            dispatch(
                addDevice({
                    deviceId: nanoToAdd,
                    roomId: null,
                })
            );
        }
        // - create room
        let roomRes = await createRoom({
            room_id: newRoom.roomId,
            room_name: newRoom.roomName,
            floor: newRoom.floor,
            nano_id: nanoToAdd,
            building_id: option === 0 ? selectedBuilding.buildingId : newBuilding.buildingId,
        });

        if (roomRes.data.networkError) {
            enqueueSnackbar(roomRes.data.message, { variant: "error" });
            setCommitConfig(false);
            return;
        }
        if (!roomRes.data.indicator) {
            enqueueSnackbar(
                "Failed to add room. This is possibly due to duplicated room id or room name. Sensors and devices are not added.",
                { variant: "error" }
            );
            setCommitConfig(false);
            return;
        }
        dispatch(
            addRoom({
                roomId: newRoom.roomId,
                roomName: newRoom.roomName,
                floor: newRoom.floor,
                nanoId: nanoToAdd,
                buildingId: option === 0 ? selectedBuilding.buildingId : newBuilding.buildingId,
            })
        );

        if (userData.permission !== PERMISSION.SUPER) {
            let payload = [];
            payload.push({
                user_id: userData.userId,
                room_id: newRoom.roomId,
            });
            let res = await addAuthorization({ payload: payload });
            if (!res.data.indicator) {
                enqueueSnackbar(res.data.message, { variant: "error" });
                setCommitConfig(false);
                return;
            }
            enqueueSnackbar("You are now authorized to view " + newRoom.roomName, {
                variant: "success",
            });
        }

        // - create sensors
        for (let s of newSensors) {
            let sRes = await createSensor({
                sensor_id: s.sensorId,
                sensor_name: s.sensorName,
                sensor_type: s.type,
                room_id: newRoom.roomId,
            });
            if (sRes.data.networkError) {
                enqueueSnackbar(sRes.data.message, { variant: "error" });
                setCommitConfig(false);
                return;
            }
            if (!sRes.data.indicator) {
                enqueueSnackbar(`Failed to add sensor ${s.sensorName}. This is possibly due to duplicated sensor id.`, {
                    variant: "error",
                });
                continue;
            }
            dispatch(
                addSensor({
                    sensorId: s.sensorId,
                    sensorName: s.sensorName,
                    sensorType: s.type,
                    roomId: newRoom.roomId,
                })
            );
        }
        // Create devices
        for (let key in newDevices) {
            if (key === "n") continue;
            let dRes = await createDevice({
                device_id: newDevices[key],
                room_id: newRoom.roomId,
            });
            if (dRes.data.networkError) {
                enqueueSnackbar(dRes.data.message, { variant: "error" });
                setCommitConfig(false);
                return;
            }
            if (!dRes.data.indicator) {
                enqueueSnackbar(
                    `Failed to add device ${newDevices[key]}. This is possibly due to duplicated device id.`,
                    { variant: "error" }
                );
                continue;
            }
            dispatch(
                addDevice({
                    deviceId: newDevices[key],
                    roomId: newRoom.roomId,
                })
            );
        }

        enqueueSnackbar(`Successfully added config.`, { variant: "success" });
        resetStates();
        setCommitConfig(false);
        setOpenAddConfig(false);
    }

    const stepsTitle = ["Select Building", "Configure Room", "Configure Sensors", "Configure Devices", "Done"];
    const steps = [
        <Step1
            nextStep={nextStep}
            option={option}
            setOption={setOption}
            newBuilding={newBuilding}
            setNewBuilding={setNewBuilding}
            selectedBuilding={selectedBuilding}
            setSelectedBuilding={setSelectedBuilding}
        />,
        <Step2 nextStep={nextStep} prevStep={prevStep} newRoom={newRoom} setNewRoom={setNewRoom} />,
        <Step3 nextStep={nextStep} prevStep={prevStep} newSensors={newSensors} setNewSensors={setNewSensors} />,
        <StepAddDevice
            nextStep={nextStep}
            prevStep={prevStep}
            newDevices={newDevices}
            setNewDevices={setNewDevices}
            nanoOption={nanoOption}
            setNanoOption={setNanoOption}
            selectedNano={selectedNano}
            setSelectedNano={setSelectedNano}
        />,
        <Step4 confirmAddConfig={confirmAddConfig} prevStep={prevStep} />,
    ];
    const results = [
        `${option === 0 ? "Selected: " + selectedBuilding.buildingName : "Created " + newBuilding.buildingName}`,
        `Id: ${newRoom.roomId}, Name: ${newRoom.roomName}, Floor: ${newRoom.floor}`,
        `Sensors added: ${newSensors.length}`,
        newDevices.f.deviceId === ""
            ? ""
            : `Feedback: ${newDevices.f}, Footfall: ${newDevices.p}, Nano: ${newDevices.n || selectedNano}`,
        ``,
    ];
    return (
        <Slide direction="left" in={openAddConfig} mountOnEnter unmountOnExit>
            <div className="add-config-page">
                <div className="add-config-page-main">
                    <div className="add-config-header">
                        <h3>Configure Room</h3>
                        <FaTimesCircle
                            className="add-config-close-btn"
                            onClick={() => {
                                resetStates();
                                setOpenAddConfig(false);
                                if (!commitConfig) {
                                    enqueueSnackbar("Configuration discarded.", {
                                        variant: "info",
                                    });
                                }
                            }}
                        />
                    </div>

                    <div className="add-config-content-wrapper">
                        {steps.map((element, index) => {
                            return (
                                <Step
                                    currStep={step}
                                    key={"Step" + index}
                                    index={index}
                                    title={stepsTitle[index]}
                                    result={results[index]}
                                >
                                    {element}
                                </Step>
                            );
                        })}
                    </div>
                </div>
            </div>
        </Slide>
    );
};

export default AddConfig;
